import React from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { classNames } from '../../lib/utils';
import { getPersonInitials } from '../../lib/personOkrIndex';
import { PERMISSION_ROLES } from '../../lib/employeeAdmin';
import EmployeeDetailPanel from './EmployeeDetailPanel';

/**
 * One person in the list: a click anywhere on the row expands their details.
 *
 * Delete is disabled for the signed-in admin themselves and for the last
 * remaining admin — both leave nobody able to undo the mistake.
 */
export default function EmployeeRow({
  user, users, teams, krCodeIndex, isExpanded, onToggle, onDelete, onUpdateManager, isSelf, isLastAdmin,
}) {
  const isPermissionRole = PERMISSION_ROLES.includes(user.role);
  const deleteBlockedReason = isSelf
    ? 'You cannot delete your own account'
    : (isLastAdmin ? 'This is the last admin — promote someone else first' : null);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/70 transition-colors">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          {isExpanded
            ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
            : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold flex-shrink-0">
            {getPersonInitials(user.name || user.email)}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-gray-800 truncate">
              {user.name || <span className="italic text-gray-400">No name</span>}
              {isSelf && <span className="ml-2 text-[10px] font-bold text-gray-400 uppercase">you</span>}
            </span>
            <span className="block text-[11px] text-gray-500 truncate">{user.email}</span>
          </span>
          <span className="hidden sm:block text-[11px] text-gray-500 truncate max-w-[12rem]">{user.role}</span>
          {isPermissionRole && (
            <span className="hidden sm:inline text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">
              {user.role}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => onDelete(user)}
          disabled={!!deleteBlockedReason}
          title={deleteBlockedReason || `Remove ${user.name || user.email}`}
          className={classNames(
            'p-1.5 rounded-lg flex-shrink-0 transition-colors',
            deleteBlockedReason
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
          )}
        >
          <Trash2 size={15} />
        </button>
      </div>

      {isExpanded && (
        <EmployeeDetailPanel
          user={user}
          users={users}
          teams={teams}
          krCodeIndex={krCodeIndex}
          onUpdateManager={onUpdateManager}
        />
      )}
    </div>
  );
}
